import { useLocale } from "@/composables/useLocale"
import { reconfigureRoutes } from "@/router/reconfigureRoutes"
import VfFormCollection from "@/vf/components/crud/form/VfFormCollection.vue"
import { type AppConfig, useAppConfig } from "@/vf/composables"
import { registerVfPlugins } from "@/vf/plugins"
import { plugin as formkit } from "@formkit/vue"
import * as Sentry from "@sentry/vue"
import { Settings } from "luxon"
import { createPinia } from "pinia"
import { createApp, watch } from "vue"
import App from "./App.vue"
import VarsityContentProcessor from "./components/VarsityContentProcessor.vue"
import VarsityHintBox from "./components/VarsityHintBox.vue"
import VarsityFeedbackLoading from "./components/feedback/VarsityFeedbackLoading.vue"
import CheckboxToggle from "./components/form/CheckboxToggle.vue"
import TranslationsForm from "./components/form/TranslationsForm.vue"
import { registerYouTubePlaceholder } from "./components/quill/directives"
import { useServerSideLocales } from "./composables/useServerSideLocales"
import createFormkitConfig from "./formkit.config"
import { registerI18n } from "./plugins/i18n"
import router from "./router"

// All dates should be shown in server time (Europe/Berlin), regardless of the browser's time zone
Settings.defaultZone = "Europe/Berlin"

const app = createApp(App)

const pinia = createPinia()

app.use(pinia)

registerVfPlugins(app)
registerYouTubePlaceholder(app)

if (location.hostname !== "localhost") {
    Sentry.init({
        app,
        dsn: "https://28deca79df8b382b19c75680f8df9ece@o4506948613898240.ingest.us.sentry.io/4507220044546048",
        release: "varsity-web@" + import.meta.env.VITE_SENTRY_RELEASE,
        integrations: [Sentry.replayIntegration()],
        environment: import.meta.env.VITE_ENVIRONMENT ?? "dev",
        // Session Replay
        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        ignoreErrors: [/^Request failed with status code 401$/, /^Network Error$/],
    })
}

function applySentryUser(appConfig: AppConfig) {
    const user = appConfig.login
        ? {
              email: appConfig.login.email,
              username: appConfig.login.username,
              id: appConfig.login.id,
          }
        : null

    console.log("Sentry user", user)
    Sentry.setUser(user)
}

// pre-load translations while the rest of the app is loading
const loadLocales = useServerSideLocales()

useAppConfig()
    .loadInitialAppConfig(router)
    .then(async appConfig => {
        watch(appConfig, newAppConfig => {
            reconfigureRoutes(router, newAppConfig.routes)
            applySentryUser(newAppConfig)
        })
        reconfigureRoutes(router, appConfig.value.routes)
        applySentryUser(appConfig.value)
        app.use(router)
        ;(window as any).router = router
        ;(window as any).vue = app

        // get the locale storage (local storage item that holds the users local)
        const { changeLocale, locale: localStorageLocale } = useLocale()

        // if logged in, use the user locale, otherwise use the local storage locale
        const locale = appConfig.value?.login?.locale ?? localStorageLocale.value

        // await the messages that are being pre-loaded above, because we need them now at latest
        const { messages } = await loadLocales

        // create vue-i18n and register it to the vue app and to the window object
        const i18n = registerI18n(messages, app, locale)

        // update the locale storage if the user-provided locale differs from the one in the local storage
        changeLocale(locale)

        app.use(formkit, createFormkitConfig(i18n))

        app.component("VarsityContentProcessor", VarsityContentProcessor)
        app.component("VarsityFeedbackLoading", VarsityFeedbackLoading)
        app.component("CheckboxToggle", CheckboxToggle)
        app.component("VarsityHintBox", VarsityHintBox)
        app.component("TranslationsForm", TranslationsForm)
        app.component("VfFormCollection", VfFormCollection)
        app.mount("#app")
    })
